/**
 * CityLabels - Billboarded text labels for city names.
 *
 * Renders city names as 3D text that always faces the camera,
 * positioned at each city's geographic coordinates.
 * Shows fewer cities when zoomed out, more when zoomed in.
 */

import { useMemo } from "react";

import { GLOBE_CONFIG } from "@/domain/globe/constants";
import { filterCitiesByZoom } from "@/domain/globe/constants/cityVisibility";
import { Html } from "@react-three/drei";

import type { CityData } from '@/domain/globe/types/features'

interface CityLabelsProps {
  /** Array of city data with names and coordinates */
  cities: CityData[]
  /** Camera center latitude */
  centerLat: number
  /** Camera center longitude */
  centerLng: number
  /** Current zoom level (affects which labels are shown) */
  zoom: number
}

/**
 * Convert lat/lng to 3D world position.
 * Uses the same coordinate system as CountryGlobe.
 */
function latLngToWorldPos(lat: number, lng: number, radius: number): [number, number, number] {
  const latRad = (lat * Math.PI) / 180
  const lngRad = (lng * Math.PI) / 180
  return [
    radius * Math.cos(latRad) * Math.sin(lngRad),
    radius * Math.sin(latRad),
    -radius * Math.cos(latRad) * Math.cos(lngRad),
  ]
}

/**
 * Calculate angular distance between two lat/lng points (in degrees).
 * Uses spherical law of cosines.
 */
function angularDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const lat1Rad = (lat1 * Math.PI) / 180
  const lat2Rad = (lat2 * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180

  const cosAngle =
    Math.sin(lat1Rad) * Math.sin(lat2Rad) + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng)

  // Clamp to avoid floating point errors with acos
  return (Math.acos(Math.max(-1, Math.min(1, cosAngle))) * 180) / Math.PI
}

/**
 * CityLabels component - renders billboarded text for cities.
 * Uses Html for constant screen-space size regardless of zoom.
 * Filters cities based on zoom level and visibility range.
 */
export function CityLabels({ cities, centerLat, centerLng, zoom }: CityLabelsProps) {
  // Position city labels at globe surface (slightly inside to avoid z-fighting)
  const labelRadius = GLOBE_CONFIG.SPHERE_RADIUS - 0.001

  // Filter cities by zoom level first
  const zoomFilteredCities = useMemo(
    () => filterCitiesByZoom(cities, zoom),
    [cities, zoom]
  )

  // Memoize label data with positions
  const labelData = useMemo(() => {
    return zoomFilteredCities.map((city) => {
      const position = latLngToWorldPos(city.lat, city.lng, labelRadius)
      return {
        name: city.name,
        position,
        lat: city.lat,
        lng: city.lng,
        isCapital: city.isCapital,
        worldcity: city.worldcity,
        scalerank: city.scalerank,
      }
    })
  }, [zoomFilteredCities, labelRadius])

  // Filter to only labels within visible angular distance
  const visibleLabels = labelData.filter(({ lat, lng }) => {
    const distance = angularDistance(centerLat, centerLng, lat, lng)
    return distance <= GLOBE_CONFIG.LABEL_MAX_VISIBLE_ANGLE
  })

  // Get dot size in pixels based on importance
  const getDotSize = (scalerank: number, worldcity: boolean, isCapital: boolean): number => {
    if (worldcity) return 6
    if (isCapital) return 5
    if (scalerank <= 1) return 4
    if (scalerank <= 2) return 3.5
    return 3
  }

  // Get font size based on importance
  const getFontSize = (scalerank: number, worldcity: boolean, isCapital: boolean): string => {
    if (worldcity) return '10px'
    if (isCapital) return '9px'
    if (scalerank <= 1) return '8px'
    if (scalerank <= 2) return '7px'
    return '6px'
  }

  // Get font weight based on importance
  const getFontWeight = (worldcity: boolean, isCapital: boolean): number => {
    if (worldcity) return 600
    if (isCapital) return 500
    return 400
  }

  return (
    <>
      {visibleLabels.map(({ name, position, isCapital, worldcity, scalerank, lat, lng }) => (
        <Html
          key={`${name}-${lat}-${lng}`}
          position={position}
          zIndexRange={[5, 5]}
          style={{
            pointerEvents: 'none',
            userSelect: 'none',
            transform: 'translate(-50%, 0)', // Center horizontally, anchor at top (dot position)
          }}
        >
          {/* City dot - billboarded, constant screen size */}
          <div
            style={{
              width: `${getDotSize(scalerank, worldcity, isCapital)}px`,
              height: `${getDotSize(scalerank, worldcity, isCapital)}px`,
              backgroundColor: '#444444',
              borderRadius: '50%',
              margin: '0 auto 2px auto',
            }}
          />
          {/* City label */}
          <div
            style={{
              color: 'black',
              fontSize: getFontSize(scalerank, worldcity, isCapital),
              fontWeight: getFontWeight(worldcity, isCapital),
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {name}
          </div>
        </Html>
      ))}
    </>
  )
}
